<template>
  <!--
    Neutral "content unavailable" state shown on PUBLIC surfaces (embed/share/site)
    when the owner's credit wallet is empty and the backend withholds playback
    (PlaybackInfo.blocked). Deliberately does NOT expose the billing reason to the
    public viewer (docs/plans/billing-ui.md 4.4, board VisitorUnavailable).
    RGAA: announced to assistive tech (role=status + aria-live), icon hidden from
    the a11y tree, high-contrast text.
  -->
  <div class="player-unavailable" role="status" aria-live="polite">
    <v-icon size="40" :icon="mdiEyeOffOutline" class="player-unavailable__icon" aria-hidden="true" />
    <p class="player-unavailable__title">{{ title }}</p>
    <p v-if="!props.message" class="player-unavailable__text">{{ copy.hint }}</p>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { mdiEyeOffOutline } from '@mdi/js'
  import { currentLocale } from '../locale'

  // Localized to the VIEWER's browser language (shared player locale: fr/en, en
  // fallback). `media` names what is withheld — a video player says "video", the
  // others "content" — and `message` lets a host override the copy whole.
  const props = withDefaults(defineProps<{ message?: string; media?: 'video' | 'content' }>(), {
    message: '',
    media: 'content',
  })

  const COPY = {
    fr: {
      video: 'Cette vidéo est momentanément indisponible',
      content: 'Ce contenu est momentanément indisponible',
      hint: 'Réessayez un peu plus tard. Si le problème persiste, contactez l’éditeur du site.',
    },
    en: {
      video: 'This video is temporarily unavailable',
      content: 'This content is temporarily unavailable',
      hint: 'Try again a little later. If the problem persists, contact the site’s publisher.',
    },
  } as const

  const copy = computed(() => (currentLocale.value === 'fr' ? COPY.fr : COPY.en))
  const title = computed(() => props.message || copy.value[props.media])
</script>

<style scoped>
  .player-unavailable {
    width: 100%;
    height: 100%;
    min-height: 180px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 24px;
    text-align: center;
    background: #0a0a0a;
    color: #e5e5e5;
  }

  .player-unavailable__icon {
    color: #9ca3af;
  }

  .player-unavailable__title {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.4;
    max-width: 36ch;
  }

  .player-unavailable__text {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: #b4bccb;
    max-width: 44ch;
  }
</style>
